import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useRouter } from 'shared-routing';
import { BasketDetails } from 'shared-types/Basket';
import { Item } from 'shared-types';
import { Property } from 'shared-types/Property';
import { BasketTemplate } from 'voyager';
import type { BasketContentsView, RecommendationItem } from 'voyager';
import { Routes } from '@common/constants';
import { startSinglePageCheckout } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { modalErrorSelectors } from '@store/modalError/selectors';
import { ReduxPageType } from '@common-types/ReduxPage';
import { useLabels } from '@features/Menu/useLabels';
import { analytics, formatBasketItems, formatPrice } from '@features/analytics';
import { useTranslations } from '@lang';
import { getItineraryItems } from '@utils/getItineraryView';
import { mapModules } from '../../../common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '../../../common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '../../../common/components/BasketDrawer/hooks/useModulesToRecommend';
import { toBasketCardItems } from '../../../common/components/BasketDrawer/v2/adapters/toBasketCardItem';
import { toItineraryGuests } from '../../../common/components/BasketDrawer/v2/adapters/toItineraryGuests';
import { useRemoveBasketItem } from '../hooks/useRemoveBasketItem';
import { useDiscountCodes } from './useDiscountCodes';

type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

const EMPTY_ITEMS: Item[] = [];

type BasketV2ConnectedProps = {
  language: string;
  checkoutRoute: string;
  isSinglePageCheckout: boolean;
};

export const BasketV2Connected: FC<BasketV2ConnectedProps> = ({
  language,
  checkoutRoute,
  isSinglePageCheckout,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();
  const labels = useLabels();
  const property = useSelector(entitySelectors.getProperty) as Property;
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails;
  const unacknowledgedError = useSelector(modalErrorSelectors.getModalError);
  const spaPage = useSelector(entitySelectors.getSpaPage) as ReduxPageType;
  const tablesPage = useSelector(entitySelectors.getTablesPage) as ReduxPageType;
  const roomsPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'rooms')
  ) as ReduxPageType;
  const golfPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'golf')
  ) as ReduxPageType;
  const spaTreatmentsPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'spa-treatments')
  ) as ReduxPageType;
  const spaDaysPage = useSelector(entitySelectors.getSpaDaysPage) as ReduxPageType;
  const spaClassesPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'spa-classes')
  ) as ReduxPageType;
  const spaFacilitiesPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'spa-facilities')
  ) as ReduxPageType;
  const isSpaTreatmentEnabled = useSelector(entitySelectors.isTreatmentEnabled);
  const isSpaDaysEnabled = useSelector(entitySelectors.isSpaDaysEnabled);
  const isSpaClassEnabled = useSelector(entitySelectors.isClassEnabled);
  const isSpaFacilityEnabled = useSelector(entitySelectors.isFacilityEnabled);

  const [view, setView] = useState<BasketContentsView>('itemised');
  const [isCheckoutPending, setIsCheckoutPending] = useState(false);
  const { promoCode, voucherCode } = useDiscountCodes(language);
  const { removeItem, removingItemId, isUpdating, displayBasketDetails } =
    useRemoveBasketItem(language);
  const displayBasket = displayBasketDetails;

  const propertyModules = useMemo(
    () => Object.keys(property?.modulesExcludingDisabled ?? {}) as ModuleType[],
    [property?.modulesExcludingDisabled]
  );

  const guestItineraries = useMemo(
    () => getItineraryItems(displayBasket?.items ?? EMPTY_ITEMS, t, labels),
    [displayBasket?.items, t, labels]
  );

  const items = useMemo(
    () =>
      toBasketCardItems(displayBasket?.items ?? EMPTY_ITEMS, {
        t,
        currencyCode: displayBasket?.total?.currencyCode ?? '',
        moduleImages: {
          rooms: roomsPage?.heroBackgroundImage?.url,
          spa: spaPage?.heroBackgroundImage?.url,
          tables: tablesPage?.heroBackgroundImage?.url,
        },
      }),
    [
      displayBasket?.items,
      displayBasket?.total?.currencyCode,
      t,
      roomsPage?.heroBackgroundImage?.url,
      spaPage?.heroBackgroundImage?.url,
      tablesPage?.heroBackgroundImage?.url,
    ]
  );

  const guests = useMemo(() => toItineraryGuests(guestItineraries), [guestItineraries]);

  const recommendedModules = useModulesToRecommend({
    modules: propertyModules,
  });

  const { recommendations, routeById } = useMemo(() => {
    const basketItems = displayBasket?.items ?? EMPTY_ITEMS;
    const hasItemOfType = (predicate: (type?: string) => boolean) =>
      basketItems.some((item) => predicate(item.type));
    const pages = mapModules(
      recommendedModules.filter((m) => m !== 'spa'),
      { spaPage, tablesPage, roomsPage, golfPage }
    ).filter((page): page is NonNullable<typeof page> => page != null);

    if (recommendedModules.includes('spa')) {
      const spaSubtypes: Array<{
        enabled: boolean;
        page: ReduxPageType | undefined;
        route: Routes;
        itemTypeMatch: (type?: string) => boolean;
      }> = [
        {
          enabled: Boolean(isSpaTreatmentEnabled),
          page: spaTreatmentsPage,
          route: Routes.SPA_TREATMENT_LIST,
          itemTypeMatch: (type) => type === 'spa-treatments',
        },
        {
          enabled: Boolean(isSpaDaysEnabled),
          page: spaDaysPage,
          route: Routes.SPA_DAY_LIST,
          itemTypeMatch: (type) => type === 'spa-days' || type === 'spa-packages',
        },
        {
          enabled: Boolean(isSpaClassEnabled),
          page: spaClassesPage,
          route: Routes.SPA_CLASSES_LIST,
          itemTypeMatch: (type) => type === 'spa-classes',
        },
        {
          enabled: Boolean(isSpaFacilityEnabled),
          page: spaFacilitiesPage,
          route: Routes.FACILITY_LIST,
          itemTypeMatch: (type) => type === 'spa-facilities',
        },
      ];
      for (const { enabled, page, route, itemTypeMatch } of spaSubtypes) {
        if (enabled && page && !hasItemOfType(itemTypeMatch)) {
          pages.push({ ...page, route } as (typeof pages)[number]);
        }
      }
    }

    const recs: RecommendationItem[] = pages.map((page) => ({
      id: String(page.id),
      name: page.title,
      imageUrl: page.heroBackgroundImage?.url,
    }));
    const map = new Map<string, string>(pages.map((page) => [String(page.id), page.route]));
    return { recommendations: recs, routeById: map };
  }, [
    recommendedModules,
    spaPage,
    tablesPage,
    roomsPage,
    golfPage,
    spaTreatmentsPage,
    spaDaysPage,
    spaClassesPage,
    spaFacilitiesPage,
    isSpaTreatmentEnabled,
    isSpaDaysEnabled,
    isSpaClassEnabled,
    isSpaFacilityEnabled,
    displayBasket?.items,
  ]);

  const hasTrackedViewCart = useRef(false);
  useEffect(() => {
    if (hasTrackedViewCart.current || !basketDetails?.total) return;
    hasTrackedViewCart.current = true;
    analytics.track({
      event: 'view_cart',
      params: {
        currency: basketDetails.total.currencyCode,
        value: formatPrice(basketDetails.total.amount),
        items: formatBasketItems(basketDetails.items),
      },
    });
  }, [basketDetails?.total, basketDetails?.items]);

  const promotionsDiscount = useMemo(() => {
    const promo = displayBasket?.activePromo;
    if (!promo) return undefined;
    if (promo.discountValue.type === 'price') {
      const { amount, currencyCode } = promo.discountValue.value;
      return amount > 0 ? { amount, currencyCode } : undefined;
    }
    const standardTotal = displayBasket?.discountTotals?.standardTotal;
    if (!standardTotal) return undefined;
    const amount = Math.round((standardTotal.amount * promo.discountValue.value) / 100);
    return amount > 0 ? { amount, currencyCode: standardTotal.currencyCode } : undefined;
  }, [displayBasket?.activePromo, displayBasket?.discountTotals?.standardTotal]);

  const vouchersDiscount = useMemo(() => {
    const vouchers = displayBasket?.vouchers ?? [];
    if (vouchers.length === 0) return undefined;
    const amount = vouchers.reduce((sum, v) => sum + (v.redemptionTotal?.amount ?? 0), 0);
    if (amount <= 0) return undefined;
    return { amount, currencyCode: vouchers[0].redemptionTotal.currencyCode };
  }, [displayBasket?.vouchers]);

  const subtotalValue = displayBasket?.discountTotals?.standardTotal;

  if (!basketDetails) return null;

  return (
    <BasketTemplate
      items={items}
      totalPrice={{
        amount: displayBasket.total.amount,
        currencyCode: displayBasket.total.currencyCode,
      }}
      subtotal={subtotalValue}
      promotionsDiscount={promotionsDiscount}
      vouchersDiscount={vouchersDiscount}
      view={view}
      onViewChange={setView}
      guests={guests}
      promoCode={promoCode}
      voucherCode={voucherCode}
      recommendations={recommendations}
      onRecommendationSelect={(id) => {
        const route = routeById.get(id);
        if (route) router.push({ pathname: route });
      }}
      onRemoveItem={removeItem}
      removingItemId={removingItemId}
      isUpdating={isUpdating}
      emptyMessage={t('basket.emptyBasketMessage')}
      onContinueShopping={() => router.push({ pathname: Routes.HOME })}
      isCheckoutPending={isCheckoutPending}
      isCheckoutBlocked={Boolean(unacknowledgedError)}
      onCheckout={async () => {
        if (isCheckoutPending) return;
        setIsCheckoutPending(true);
        analytics.track({
          event: 'begin_checkout',
          params: {
            currency: basketDetails.total.currencyCode,
            value: formatPrice(basketDetails.total.amount),
            items: formatBasketItems(basketDetails.items),
            coupon: basketDetails.promoCode || undefined,
          },
        });

        if (!isSinglePageCheckout) {
          router.push({ pathname: checkoutRoute });
          return;
        }

        const hasExpiredItems = basketDetails.items?.some(
          (item) => item.expireAt && Date.now() > Date.parse(item.expireAt)
        );
        if (hasExpiredItems) {
          setIsCheckoutPending(false);
          return;
        }

        try {
          const result = (await dispatch(startSinglePageCheckout(language))) as unknown as
            | { orderReference?: string }
            | undefined;

          if (result?.orderReference) {
            router.push({ pathname: checkoutRoute });
          } else {
            setIsCheckoutPending(false);
          }
        } catch {
          setIsCheckoutPending(false);
        }
      }}
      labels={{
        totalHeadingPrefix: t('basket.totalHeadingPrefix'),
        emptyHeading: t('basket.emptyBasket'),
        continueShopping: t('basket.continueShopping'),
        checkout: t('basket.checkout'),
        basketHeading: t('basket.heading'),
        itemisedView: t('basket.itemisedView'),
        itineraryView: t('basket.itineraryView'),
        addedConfirmation: t('basket.addedConfirmationHeader'),
        subtotal: t('basket.subtotal'),
        promotions: t('basket.promotions'),
        vouchers: t('basket.vouchers'),
        total: t('basket.total'),
        removeLabel: t('common.remove'),
        removing: t('basket.removingItem'),
        noImageLabel: t('basket.noImageLabel'),
        selectGuestLabel: t('basket.selectGuestLabel'),
        recommendationsHeadingPrimary: t('basket.recommendationsHeadingPrimary'),
        recommendationsHeadingSecondary: t('basket.recommendationsHeadingSecondary'),
        fromPriceLabel: t('basket.fromPriceLabel'),
      }}
      locale={language}
    />
  );
};
